import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import Navbar from "@/pages/home/components/Navbar";
import CTASection from "@/pages/home/components/CTASection";
import Footer from "@/pages/home/components/Footer";
import NotFound from "@/pages/NotFound";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://itaobuy.quest";

const articleKeywords: Record<string, string> = {
  "itaobuy-sheet-guide": "itaobuy sheet guide, product sourcing spreadsheet, dropshipping tutorial, china product sourcing",
  "itaobuy-sheet-vs-aliexpress": "itaobuy sheet vs aliexpress, dropshipping sourcing comparison, product research platforms",
  "itaobuy-sheet-winning-products": "find viral products, winning products dropshipping, itaobuy sheet product research",
  "itaobuy-sheet-beginner": "itaobuy sheet beginner, dropshipping for beginners, product sourcing tutorial",
  "itaobuy-sheet-profit-margins": "profit margins dropshipping, itaobuy sheet profit, maximize sourcing profit",
  "itaobuy-sheet-supplier-tips": "supplier negotiation, dropshipping supplier vetting, itaobuy sheet suppliers",
  "itaobuy-sheet-shopify-integration": "itaobuy sheet shopify, shopify product sourcing, spreadsheet to store",
  "itaobuy-sheet-trending-summer-2026": "trending products 2026, itaobuy sheet trends, seasonal dropshipping categories",
  "itaobuy-sheet-faq-sellers": "itaobuy sheet FAQ, dropshipping questions, product sourcing FAQ",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const canonicalUrl = `${siteUrl}/${article.slug}`;
  const keywords = articleKeywords[article.slug] || "itaobuy sheet, dropshipping, product sourcing";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "url": canonicalUrl,
    "datePublished": "2026-06-28",
    "dateModified": "2026-06-28",
    "author": {
      "@type": "Organization",
      "name": "Itaobuy Sheet",
      "url": `${siteUrl}/`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Itaobuy Sheet",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  const faqSchema = article.slug === "itaobuy-sheet-faq-sellers" ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.sections.map((section) => ({
      "@type": "Question",
      "name": section.heading,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": section.content
      }
    }))
  } : null;

  return (
    <>
      <title>{article.title}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.image} />
      <Navbar />
      <main>
        <article>
          <header className="bg-background-100 pt-[120px] pb-16 md:pb-20 px-6 md:px-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="max-w-[720px]">
                <p className="text-xs tracking-[0.25em] uppercase text-foreground-500 mb-4 font-label">
                  Sourcing Guide &middot; {article.readTime}
                </p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground-950 leading-[1.1]">
                  {article.title}
                </h1>
                <p className="mt-4 text-lg text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-medium hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Home
                </Link>
              </div>
            </div>
          </header>

          <div className="bg-background-50 py-16 md:py-20 px-6 md:px-10">
            <div className="max-w-[720px] mx-auto">
              <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
                {article.sections.map((section, idx) => (
                  <section key={idx}>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      {section.heading}
                    </h2>
                    {section.content && (
                      <p className="text-base md:text-lg">
                        {section.content.includes("itaobuy sheet home page") ? (
                          <>
                            {section.content.split("itaobuy sheet home page")[0]}
                            <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">itaobuy sheet home page</Link>
                            {section.content.split("itaobuy sheet home page")[1] || ""}
                          </>
                        ) : section.content.includes("all product categories") ? (
                          <>
                            {section.content.split("all product categories")[0]}
                            <Link to="/#categories" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">all product categories</Link>
                            {section.content.split("all product categories")[1] || ""}
                          </>
                        ) : section.content.includes("itaobuy.quest main site") ? (
                          <>
                            {section.content.split("itaobuy.quest main site")[0]}
                            <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">itaobuy.quest main site</Link>
                            {section.content.split("itaobuy.quest main site")[1] || ""}
                          </>
                        ) : (
                          section.content
                        )}
                      </p>
                    )}
                    {section.items && section.items.length > 0 && (
                      <ol className="list-decimal pl-5 space-y-3 text-base md:text-lg mt-4">
                        {section.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ol>
                    )}
                  </section>
                ))}

                <div className="rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 my-12">
                  <div>
                    <p className="font-heading text-lg font-semibold text-foreground-900">Ready to start sourcing?</p>
                    <p className="text-sm text-foreground-600 mt-1">Explore the full itaobuy sheet or browse more guides.</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap justify-center">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 text-sm font-medium hover:border-foreground-900 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Back to Home
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Open Live Sheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                </div>

                <p className="text-base md:text-lg">
                  The bottom line is that sourcing does not have to be complicated. With the right tools and a systematic approach, you can consistently find products that sell well. Visit the{" "}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 underline cursor-pointer">itaobuy.quest main site</Link>{" "}
                  to explore the full category selection and start your sourcing journey today.
                </p>
              </div>
            </div>
          </div>
        </article>

        <CTASection />
      </main>
      <Footer />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </>
  );
}